// Credential masking utility (SIM-4 / ACS-4128): shared display-only masking for a customer's
// email or phone across the sign-in flow (recognized prefill, OTP destination, disambiguation).
// BR-019 fixed format; BR-021 the full value is never emitted. Pure, isomorphic, no logging.

// Fixed 5-star block for the email local part — deliberately constant so the local-part length
// is never revealed (BR-021).
const EMAIL_STAR_BLOCK = '*****';
const PHONE_VISIBLE_DIGITS = 4;

/**
 * Mask an email for display as `r*****@gmail.com` — the first character of the local part,
 * a fixed five-asterisk block, then `@` and the domain segment that follows the first `@`.
 * The star block is constant (never derived from the local-part length) so the value's length
 * is not leaked (BR-019/BR-021).
 * @param {string} [email] - The email to mask (already in memory; never logged).
 * @returns {string} The masked email, or '' for empty / non-string / malformed input.
 */
const maskEmail = email => {
    // Guard non-string (and empty) input so a stray Redux/API value can never throw in the UI.
    if (typeof email !== 'string' || !email) {
        return '';
    }

    const atIndex = email.indexOf('@');

    // No '@', or an empty local part ('@x.com') — nothing safe to display.
    if (atIndex < 1) {
        return '';
    }

    const localFirstChar = email[0];
    const domain = email.slice(atIndex + 1).split('@')[0];

    // Empty domain ('a@' or 'a@@x.com') — malformed, nothing safe to display.
    if (!domain) {
        return '';
    }

    return `${localFirstChar}${EMAIL_STAR_BLOCK}@${domain}`;
};

/**
 * Mask a phone for display as `(***) ***-1234` — only the last four digits are shown. Non-digit
 * characters are stripped first, so E.164 (`+14165551234`) and formatted (`(416) 555-1234`)
 * inputs both mask identically (BR-019).
 * @param {string} [phone] - The phone to mask (assumed E.164-normalized upstream; never logged).
 * @returns {string} The masked phone, or '' for non-string / fewer-than-four-digit input.
 */
const maskPhone = phone => {
    // Guard non-string (and empty) input so a stray Redux/API value can never throw in the UI.
    if (typeof phone !== 'string' || !phone) {
        return '';
    }

    const digits = phone.replace(/\D/g, '');

    if (digits.length < PHONE_VISIBLE_DIGITS) {
        return '';
    }

    const lastFour = digits.slice(-PHONE_VISIBLE_DIGITS);

    return `(***) ***-${lastFour}`;
};

export {
    maskEmail, maskPhone
};
